import { useState, useEffect, useRef } from "react";
import axios from "axios";

export default function IftarDashboardNew() {
  const [status, setStatus]           = useState("idle");
  const [speakingRow, setSpeakingRow] = useState(null);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [aiLiveData, setAiLiveData]   = useState(null);
  const [questions, setQuestions]     = useState([]);

  // ── MODE: "generate" | "final" — polling dari DB, source of truth utama ──
  const [mode, setMode]               = useState("generate");
  const modeRef                       = useRef("generate");

  const aiLiveReadyRef        = useRef(false);
  const summarizeReadyRef     = useRef(false);
  const lastAiLiveCommentRef  = useRef(null);
  const prevStatusRef         = useRef(null);
  const currentAudioRef       = useRef(null);
  const lastSpeakingRowRef    = useRef(null);
  const audioContextRef       = useRef(null);

  // ── Refs for AI Commentary TTS ─────────────────────────────────────────
  const lastAiLiveAudioRef  = useRef(null);
  const lastAiLiveSpokenRef = useRef(null);

  const PORTAL = process.env.REACT_APP_PORTAL_DESTINATION;
  const PATH   = process.env.REACT_APP_PATH_DESTINATION;

  // ── Derived: apakah ini final mode? ───────────────────────────────────
  const isFinalMode = mode === "final";

  // ── Audio context ──────────────────────────────────────────────────────
  const getAudioContext = async () => {
    if (!audioContextRef.current)
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = audioContextRef.current;
    if (ctx.state === "suspended") await ctx.resume();
    return ctx;
  };

  useEffect(() => {
    const unlock = async () => {
      if (audioContextRef.current?.state === "suspended")
        await audioContextRef.current.resume().catch(() => {});
    };
    window.addEventListener("click",      unlock);
    window.addEventListener("keydown",    unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("click",      unlock);
      window.removeEventListener("keydown",    unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);

  useEffect(() => () => audioContextRef.current?.close().catch(() => {}), []);

  // ── Status helpers ─────────────────────────────────────────────────────
  const checkBothReady = () => {
    if (modeRef.current === "final") return;
    if (aiLiveReadyRef.current && summarizeReadyRef.current) setStatus("ready");
  };

  const checkFinalReady = () => {
    if (aiLiveReadyRef.current) setStatus("ready");
  };

  const resetToProcessing = () => {
    aiLiveReadyRef.current       = false;
    summarizeReadyRef.current    = false;
    lastAiLiveCommentRef.current = null;
    setAiLiveData(null);
    setQuestions([]);
    setSpeakingRow(null);
    setStatus("processing");
  };

  // ── Polling: Mode (source of truth) ───────────────────────────────────
  useEffect(() => {
    const poll = async () => {
      try {
        const res  = await axios.get(`${PATH}/iftarExchangeGetMode`);
        const data = res.data;
        const m    = (Array.isArray(data) ? data[0]?.mode : data?.mode) ?? "generate";
        if (m !== modeRef.current) {
          modeRef.current = m;
          setMode(m);
          if (m === "final") {
            setQuestions([]);
            summarizeReadyRef.current = false;
          }
        }
      } catch {}
    };
    const id = setInterval(poll, 800);
    poll();
    return () => clearInterval(id);
  }, [PATH]);

  // ── Polling: Status ────────────────────────────────────────────────────
  useEffect(() => {
    const poll = async () => {
      try {
        const res  = await axios.get(`${PATH}/iftarExchangeGetStatus`);
        const data = res.data;
        const s    = Number(Array.isArray(data) ? data[0]?.status : data?.status);

        if (s === 0) {
          prevStatusRef.current = 0;
          aiLiveReadyRef.current = false;
          summarizeReadyRef.current = false;
          lastAiLiveCommentRef.current = null;
          setAiLiveData(null); setQuestions([]); setSpeakingRow(null);
          setStatus("idle");

        } else if (s === 2) {
          if (prevStatusRef.current !== 2) {
            resetToProcessing();
          }
          prevStatusRef.current = 2;

        } else if (s === 1) {
          prevStatusRef.current = 1;
        }
      } catch {}
    };
    const id = setInterval(poll, 1000);
    poll();
    return () => clearInterval(id);
  }, [PATH]);

  // ── Polling: AI Live ───────────────────────────────────────────────────
  useEffect(() => {
    const poll = async () => {
      if (status !== "processing" && status !== "ready") return;
      try {
        const res = await axios.get(`${PORTAL}getAILiveDB`);
        const d   = res.data?.data;
        if (!d || !d.comment || d.active !== 1) return;
        if (lastAiLiveCommentRef.current === d.comment) return;
        lastAiLiveCommentRef.current = d.comment;

        setAiLiveData({ comment: d.comment, type: d.type || "insight" });

        if (!aiLiveReadyRef.current) {
          aiLiveReadyRef.current = true;
          if (modeRef.current === "final") {
            checkFinalReady();
          } else {
            checkBothReady();
          }
        }
      } catch {}
    };
    const id = setInterval(poll, 1000);
    poll();
    return () => clearInterval(id);
  }, [status, PORTAL]);

  // ── Polling: Summarize (skip di final mode) ────────────────────────────
  useEffect(() => {
    const poll = async () => {
      if (status !== "processing" && status !== "ready") return;
      if (modeRef.current === "final") return;
      try {
        const res  = await axios.get(`${PATH}/iftarExchangeGetSummarize`);
        const data = res.data;
        if (!Array.isArray(data) || data.length === 0) return;
        if (modeRef.current === "final") return;
        setQuestions(data.map((row, i) => ({ id: String(i + 1).padStart(2, "0"), en: row.question })));
        if (!summarizeReadyRef.current) { summarizeReadyRef.current = true; checkBothReady(); }
      } catch {}
    };
    const id = setInterval(poll, 1000);
    poll();
    return () => clearInterval(id);
  }, [status, PATH]);

  // ── Polling: Speak Row ─────────────────────────────────────────────────
  useEffect(() => {
    if (status !== "ready") return;
    const poll = async () => {
      try {
        const res      = await axios.get(`${PATH}/iftarExchangeGetSpeakRow`);
        const data     = res.data;
        const rowIndex = Array.isArray(data) ? data[0]?.rowIndex : data?.rowIndex;
        setSpeakingRow(rowIndex !== undefined && rowIndex !== null ? Number(rowIndex) : null);
      } catch {}
    };
    const id = setInterval(poll, 600);
    poll();
    return () => clearInterval(id);
  }, [status, PATH]);

  // ── Polling: Banner ────────────────────────────────────────────────────
  useEffect(() => {
    const poll = async () => {
      try {
        const res  = await axios.get(`${PATH}/iftarExchangeGetBanner`);
        const data = res.data;
        setBannerVisible(!!(Array.isArray(data) ? data[0]?.visible : data?.visible));
      } catch {}
    };
    const id = setInterval(poll, 1000);
    poll();
    return () => clearInterval(id);
  }, [PATH]);

  // ── TTS (Room Temperature + AI Commentary) ─────────────────────────────
  useEffect(() => {
    // ── Case: speakingRow = -1 → TTS AI Commentary ──
    if (speakingRow === -1) {
      if (!aiLiveData?.comment) return;
      if (aiLiveData.comment === lastAiLiveSpokenRef.current) return;
      lastAiLiveSpokenRef.current = aiLiveData.comment;

      if (lastAiLiveAudioRef.current) {
        try { lastAiLiveAudioRef.current.stop(); } catch {}
        lastAiLiveAudioRef.current = null;
      }

      // Strip HTML tags
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = aiLiveData.comment;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";
      if (!plainText.trim()) return;

      (async () => {
        try {
          const response = await fetch(`${PATH}/iftarExchangeTTS`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text: plainText }),
          });
          if (!response.ok) throw new Error("TTS AI Commentary failed");
          const ctx    = await getAudioContext();
          const source = ctx.createBufferSource();
          source.buffer = await ctx.decodeAudioData(await response.arrayBuffer());
          source.connect(ctx.destination);
          lastAiLiveAudioRef.current = source;
          source.onended = () => {
            lastAiLiveAudioRef.current = null;
            lastSpeakingRowRef.current = null;
            fetch(`${PATH}/iftarExchangeSetSpeakRow`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ rowIndex: null }),
            }).catch(() => {});
          };
          source.start(0);
        } catch {
          lastAiLiveAudioRef.current = null;
          lastSpeakingRowRef.current = null;
        }
      })();
      return;
    }

    // ── Case: speakingRow = null → stop semua ──
    if (speakingRow === null) {
      if (currentAudioRef.current) {
        try { currentAudioRef.current.stop(); } catch {}
        currentAudioRef.current = null;
      }
      if (lastAiLiveAudioRef.current) {
        try { lastAiLiveAudioRef.current.stop(); } catch {}
        lastAiLiveAudioRef.current = null;
      }
      return;
    }

    // ── Case: speakingRow >= 0 → TTS Room Temperature ──
    const isNew = speakingRow !== lastSpeakingRowRef.current;
    if (!isNew) return;
    lastSpeakingRowRef.current = speakingRow;

    const text = questions[speakingRow]?.en;
    if (!text) return;

    if (currentAudioRef.current) {
      try { currentAudioRef.current.stop(); } catch {}
      currentAudioRef.current = null;
    }

    (async () => {
      try {
        const response = await fetch(`${PATH}/iftarExchangeTTS`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
        if (!response.ok) throw new Error("TTS failed");
        const ctx    = await getAudioContext();
        const source = ctx.createBufferSource();
        source.buffer = await ctx.decodeAudioData(await response.arrayBuffer());
        source.connect(ctx.destination);
        currentAudioRef.current = source;
        source.onended = () => {
          currentAudioRef.current    = null;
          lastSpeakingRowRef.current = null;
          fetch(`${PATH}/iftarExchangeSetSpeakRow`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ rowIndex: null }),
          }).catch(() => {});
        };
        source.start(0);
      } catch {
        currentAudioRef.current    = null;
        lastSpeakingRowRef.current = null;
      }
    })();
  }, [speakingRow, questions, aiLiveData, PATH]);

  const activeQuestion = speakingRow !== null && speakingRow >= 0
    ? questions[speakingRow]
    : questions[questions.length - 1] ?? null;

  // ── Sub-components ─────────────────────────────────────────────────────
  const Corners = ({ color }) => (
    <>
      {[
        { top: 0,    left: 0,    bt: true,  bl: true,  br_: false, br: "20px 0 0 0",  bb: false },
        { top: 0,    right: 0,   bt: true,  bl: false, br_: true,  br: "0 20px 0 0",  bb: false },
        { bottom: 0, left: 0,    bt: false, bl: true,  br_: false, br: "0 0 0 20px",  bb: true  },
        { bottom: 0, right: 0,   bt: false, bl: false, br_: true,  br: "0 0 20px 0",  bb: true  },
      ].map((p, i) => (
        <div key={i} style={{
          position:  "absolute",
          width:     "clamp(70px, 5.5vw, 140px)",
          height:    "clamp(70px, 5.5vw, 140px)",
          top: p.top, left: p.left, right: p.right, bottom: p.bottom,
          borderTop:    p.bt  ? `5px solid ${color}` : "none",
          borderLeft:   p.bl  ? `5px solid ${color}` : "none",
          borderBottom: p.bb  ? `5px solid ${color}` : "none",
          borderRight:  p.br_ ? `5px solid ${color}` : "none",
          borderRadius: p.br,
          pointerEvents: "none",
        }} />
      ))}
    </>
  );

  const DecorativeBanner = ({ opacity = 0.22 }) => (
    <div style={{
      position: "absolute", bottom: 0, left: 0, right: 0,
      height: "100%", overflow: "hidden", pointerEvents: "none", zIndex: 0,
    }}>
      <img
        src="/dist/img/IftarExchangeBanner.png"
        alt=""
        style={{
          position: "absolute", bottom: 0, left: "50%",
          transform: "translateX(-50%)",
          width: "auto", height: "105%",
          objectFit: "contain", objectPosition: "bottom center",
          opacity,
          filter: "saturate(0.6) brightness(0.8)",
        }}
      />
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to bottom, #07111f 0%, rgba(7,17,31,0.45) 35%, rgba(7,17,31,0) 100%)",
      }} />
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to right, #07111f 0%, transparent 15%, transparent 85%, #07111f 100%)",
      }} />
    </div>
  );

  const OrnamentLine = ({ color = "rgba(232,200,74,0.45)" }) => (
    <div style={{
      display: "flex", alignItems: "center", gap: "20px",
      width: "clamp(400px, 42vw, 1400px)",
      margin: "0 auto",
    }}>
      <div style={{ flex: 1, height: "3px", background: `linear-gradient(to right, transparent, ${color})` }} />
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <div style={{ width: "8px",  height: "8px",  background: color, transform: "rotate(45deg)", opacity: 0.55 }} />
        <div style={{
          width: "14px", height: "14px", background: color,
          transform: "rotate(45deg)",
          boxShadow: `0 0 20px ${color}, 0 0 40px rgba(232,200,74,0.3)`,
        }} />
        <div style={{ width: "8px",  height: "8px",  background: color, transform: "rotate(45deg)", opacity: 0.55 }} />
      </div>
      <div style={{ flex: 1, height: "3px", background: `linear-gradient(to left, transparent, ${color})` }} />
    </div>
  );

  // ── Derived: apakah AI Commentary sedang di-speak ──
  const isAiLiveSpeaking = speakingRow === -1;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{
      width: "100vw", height: "100vh",
      background: "linear-gradient(180deg, #060f1c 0%, #091525 55%, #050e18 100%)",
      fontFamily: "'Segoe UI', 'Helvetica Neue', system-ui, sans-serif",
      display: "flex", flexDirection: "column",
      overflow: "hidden", boxSizing: "border-box",
      imageRendering: "crisp-edges",
      textRendering: "geometricPrecision",
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
    }}>

      {/* ── BANNER OVERLAY ── */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 50,
        pointerEvents: bannerVisible ? "auto" : "none",
        transform: bannerVisible ? "translateY(0)" : "translateY(-100%)",
        transition: "transform 0.65s cubic-bezier(0.4, 0, 0.2, 1)",
        background: "#07111f",
        overflow: "hidden",  // ← tambah ini
        }}>
        <img
            src="/dist/img/IftarExchangeBanner.png"
            alt="Iftar Exchange 2026"
            style={{ width: "100%", height: "105%", objectFit: "cover" }}
        />
        </div>

      {/* ── IDLE STATE — full viewport, position fixed ── */}
      {status === "idle" && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10,
          animation: "fadeIn 0.8s ease",
        }}>
          <img
            src="/dist/img/IftarExchangeBanner.png"
            alt="Iftar Exchange 2026"
            style={{
              width: "100%",
              height: "108%",
              objectFit: "cover",
              objectPosition: "center center",
              display: "block",
            }}
          />
        </div>
      )}

      {/* ── TOP BAR (hidden when idle) ── */}
      <div style={{
        flexShrink: 0,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "0 3.5vw",
        height: status === "idle" ? "0px" : "clamp(60px, 5.5vh, 120px)",
        borderBottom: status === "idle" ? "none" : "2px solid rgba(232,200,74,0.18)",
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(12px)",
        position: "relative",
        zIndex: 10,
        overflow: "hidden",
        transition: "height 0.4s ease, border 0.4s ease",
      }}>
        <span style={{
          fontSize: "clamp(28px, 1.5vw, 42px)",
          fontWeight: "800",
          letterSpacing: "0.38em",
          color: "rgba(255,255,255,0.55)",
          textTransform: "uppercase",
          display: "flex", alignItems: "center", gap: "16px",
        }}>
          <span style={{
            width:  "clamp(12px, 0.8vw, 20px)",
            height: "clamp(12px, 0.8vw, 20px)",
            borderRadius: "50%",
            background: "#e8c84a",
            boxShadow: `
              0 0  8px rgba(232,200,74,1),
              0 0 22px rgba(232,200,74,0.75),
              0 0 48px rgba(232,200,74,0.35)
            `,
            display: "inline-block", flexShrink: 0,
            animation: "livePulse 2.2s ease-in-out infinite",
          }} />
          ROUNDTABLE DISCUSSION
        </span>

        <span style={{
          fontSize: "clamp(24px, 1.3vw, 38px)",
          letterSpacing: "0.35em",
          color: "rgba(255,255,255,0.32)",
          textTransform: "uppercase",
          fontWeight: "600",
        }}>
          Iftar Exchange 2026
        </span>
      </div>

      {/* ── MAIN AREA ── */}
      <div style={{ flex: 1, display: "flex", minHeight: 0, position: "relative" }}>

        {/* ── PROCESSING STATE ── */}
        {status === "processing" && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 10,
            background: "#07111f",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: "5vh", animation: "fadeIn 0.6s ease",
          }}>
            <DecorativeBanner opacity={0.15} />

            <div style={{
              position: "relative", zIndex: 1,
              display: "flex", flexDirection: "column", alignItems: "center", gap: "4.5vh",
            }}>
              {/* Spinner */}
              <div style={{
                position: "relative",
                width:  "clamp(140px, 16vh, 320px)",
                height: "clamp(140px, 16vh, 320px)",
              }}>
                <div style={{
                  position: "absolute", inset: 0, borderRadius: "50%",
                  border: "6px solid rgba(232,200,74,0.14)",
                }} />
                <div style={{
                  position: "absolute", inset: 0, borderRadius: "50%",
                  border: "6px solid transparent",
                  borderTopColor: "#e8c84a",
                  borderRightColor: "rgba(232,200,74,0.4)",
                  animation: "spinCircle 1s linear infinite",
                  filter: "drop-shadow(0 0 12px rgba(232,200,74,0.7))",
                }} />
                <div style={{
                  position: "absolute", inset: "22px", borderRadius: "50%",
                  border: "3px solid rgba(232,200,74,0.1)",
                  borderBottomColor: "rgba(232,200,74,0.28)",
                  animation: "spinCircle 1.8s linear infinite reverse",
                }} />
                <div style={{
                  position: "absolute", top: "50%", left: "50%",
                  transform: "translate(-50%,-50%)",
                  width:  "clamp(14px, 1.2vw, 24px)",
                  height: "clamp(14px, 1.2vw, 24px)",
                  borderRadius: "50%",
                  background: "#e8c84a",
                  boxShadow: `
                    0 0 20px rgba(232,200,74,1),
                    0 0 50px rgba(232,200,74,0.6),
                    0 0 90px rgba(232,200,74,0.25)
                  `,
                }} />
              </div>

              {/* Processing text */}
              <div style={{ textAlign: "center" }}>
                <p style={{
                  margin: "0 0 14px",
                  fontSize: "clamp(72px, 5.5vw, 130px)",
                  fontWeight: "800",
                  letterSpacing: "0.28em",
                  color: "#e8c84a",
                  textTransform: "uppercase",
                  textShadow: `
                    0 0 50px rgba(232,200,74,0.55),
                    0 0 100px rgba(232,200,74,0.2)
                  `,
                }}>
                  Processing with AI
                </p>
                <p style={{
                  margin: 0,
                  fontSize: "clamp(40px, 3.2vw, 80px)",
                  letterSpacing: "0.26em",
                  color: "rgba(255,255,255,0.38)",
                  textTransform: "uppercase",
                  fontWeight: "500",
                }}>
                  {isFinalMode ? "Generating Final Question..." : "Generating Room Temperature..."}
                </p>
              </div>

              <div style={{ display: "flex", gap: "8vw", marginTop: "2vh" }}>
                {(isFinalMode
                  ? [{ label: "AI Analysis", ready: aiLiveReadyRef.current }]
                  : [
                      { label: "AI Analysis", ready: aiLiveReadyRef.current },
                      { label: "ROOM TEMPERATURE",   ready: summarizeReadyRef.current },
                    ]
                ).map(({ label, ready }) => (
                  <div key={label} style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: "16px",
                  }}>
                    <div style={{
                      width:  "clamp(90px, 9vh, 180px)",
                      height: "clamp(90px, 9vh, 180px)",
                      borderRadius: "50%",
                      border: `5px solid ${ready ? "rgba(110,231,183,0.75)" : "rgba(232,200,74,0.35)"}`,
                      background: ready ? "rgba(110,231,183,0.12)" : "rgba(255,255,255,0.03)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "clamp(28px, 3.5vh, 64px)",
                      transition: "all 0.5s ease",
                      boxShadow: ready
                        ? "0 0 30px rgba(110,231,183,0.4), 0 0 60px rgba(110,231,183,0.15)"
                        : "none",
                    }}>
                      {ready ? "✓" : (
                        <span style={{ animation: "dotPulse 1.4s ease-in-out infinite" }}>·</span>
                      )}
                    </div>
                    <span style={{
                      fontSize: "clamp(34px, 2.8vw, 64px)",
                      letterSpacing: "0.26em",
                      color: ready ? "rgba(110,231,183,0.8)" : "rgba(255,255,255,0.28)",
                      textTransform: "uppercase",
                      fontWeight: "600",
                      transition: "color 0.5s ease",
                    }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── READY STATE ── */}
        {status === "ready" && (
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            minHeight: 0, position: "relative",
            animation: "fadeIn 0.7s ease",
          }}>
            <DecorativeBanner opacity={0.14} />

            {/* Top ornament */}
            <div style={{
              position: "relative", zIndex: 2,
              padding: "clamp(10px, 1vh, 20px) 2vw 0",
              display: "flex", justifyContent: "center",
            }}>
              <OrnamentLine />
            </div>

            {/* ── Stacked panels ── */}
            <div style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "1.2vh",
              padding: "clamp(10px, 1vh, 18px) 2.5vw clamp(10px, 1vh, 18px)",
              minHeight: 0,
              position: "relative", zIndex: 2,
            }}>

              {/* ── TOP PANEL: AI Commentary ── */}
              <div style={{
                flex: 1,
                borderRadius: "20px",
                background: aiLiveData
                  ? isFinalMode
                    ? "rgba(110,231,183,0.05)"
                    : isAiLiveSpeaking
                      ? "rgba(232,200,74,0.07)"
                      : "rgba(232,200,74,0.04)"
                  : "rgba(255,255,255,0.02)",
                border: aiLiveData
                  ? isFinalMode
                    ? "2px solid rgba(110,231,183,0.3)"
                    : isAiLiveSpeaking
                      ? "2px solid rgba(232,200,74,0.45)"
                      : "2px solid rgba(232,200,74,0.25)"
                  : "2px solid rgba(255,255,255,0.1)",
                display: "flex", flexDirection: "column",
                overflow: "hidden", position: "relative",
                transition: "border 0.5s ease, background 0.5s ease",
              }}>
                <Corners color={isFinalMode ? "rgba(110,231,183,0.4)" : "rgba(232,200,74,0.35)"} />

                {/* Panel label */}
                <div style={{
                  position: "absolute",
                  top: "clamp(16px, 2vh, 36px)",
                  left: "50%", transform: "translateX(-50%)",
                  fontSize: "clamp(28px, 1.8vw, 52px)",
                  letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: "700",
                  color: aiLiveData
                    ? isFinalMode ? "rgba(110,231,183,0.7)" : "rgba(232,200,74,0.65)"
                    : "rgba(255,255,255,0.18)",
                  whiteSpace: "nowrap",
                  textShadow: isFinalMode
                    ? "0 0 40px rgba(110,231,183,0.45)"
                    : "0 0 40px rgba(232,200,74,0.35)",
                  display: "flex", alignItems: "center", gap: "16px",
                }}>
                  {isFinalMode ? "✦ Final Question" : "✦ AI Commentary"}
                  {/* Speaking indicator for AI Commentary */}
                  {isAiLiveSpeaking && !isFinalMode && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                      {[0,1,2,3,4].map(j => (
                        <span key={j} style={{
                          display: "inline-block",
                          width:  "clamp(4px, 0.25vw, 6px)",
                          height: "clamp(14px, 1.8vh, 36px)",
                          background: "#e8c84a",
                          borderRadius: "4px",
                          boxShadow: "0 0 8px rgba(232,200,74,0.6)",
                          animation: `waveBar 0.65s ease-in-out ${j * 0.07}s infinite alternate`,
                        }} />
                      ))}
                      <span style={{
                        marginLeft: "10px",
                        fontSize: "clamp(24px, 2.2vh, 48px)",
                        animation: "pulse 1.2s ease-in-out infinite",
                      }}>
                        🔊
                      </span>
                    </span>
                  )}
                </div>

                {/* Content area */}
                <div style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "clamp(60px, 8vh, 140px) clamp(3vw, 4vw, 8vw) clamp(16px, 2vh, 36px)",
                }}>
                  {aiLiveData? (
  <div
    key={aiLiveData.comment}
    className="ailive-content"
    style={{
      fontSize: "clamp(26px, 2.2vw, 72px)",
      fontWeight: isFinalMode ? "700" : "600",
      color: isFinalMode ? "#6ee7b7" : "rgba(255,255,255,0.95)",
      lineHeight: "1.35", textAlign: "center",
      animation: "entranceLeft 0.65s cubic-bezier(0.2,0,0,1) forwards",
      width: "100%",
      textShadow: isFinalMode
        ? "0 0 40px rgba(110,231,183,0.3)"
        : isAiLiveSpeaking
          ? "0 0 60px rgba(255,255,255,0.15)"
          : "none",
    }}
  >
    {aiLiveData.comment}
  </div>
) : (
  <p style={{
    margin: 0,
    fontSize: "clamp(20px, 1.4vw, 38px)",
    color: "rgba(255,255,255,0.15)",
    letterSpacing: "0.2em", textTransform: "uppercase",
  }}>
    Awaiting AI analysis...
  </p>
)}
                </div>

                {aiLiveData && (
                  <div style={{
                    position: "absolute", inset: 0, pointerEvents: "none",
                    background: isFinalMode
                      ? "radial-gradient(ellipse at 50% 65%, rgba(110,231,183,0.09) 0%, transparent 60%)"
                      : isAiLiveSpeaking
                        ? "radial-gradient(ellipse at 50% 65%, rgba(232,200,74,0.12) 0%, transparent 60%)"
                        : "radial-gradient(ellipse at 50% 65%, rgba(232,200,74,0.08) 0%, transparent 60%)",
                  }} />
                )}
              </div>

              {/* ── DIVIDER — hanya di generate mode ── */}
              {!isFinalMode && (
                <div style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "clamp(24px, 2.5vh, 48px)",
                  flexShrink: 0,
                  gap: "12px",
                }}>
                  <div style={{
                    flex: 1, height: "3px",
                    background: "linear-gradient(to right, transparent, rgba(232,200,74,0.25))",
                    boxShadow: "0 0 12px rgba(232,200,74,0.15)",
                  }} />
                  <div style={{
                    width:  "clamp(10px, 0.8vw, 18px)",
                    height: "clamp(10px, 0.8vw, 18px)",
                    background: "rgba(232,200,74,0.55)",
                    transform: "rotate(45deg)", flexShrink: 0,
                    boxShadow: "0 0 16px rgba(232,200,74,0.45), 0 0 32px rgba(232,200,74,0.2)",
                  }} />
                  <div style={{
                    flex: 1, height: "3px",
                    background: "linear-gradient(to left, transparent, rgba(232,200,74,0.25))",
                    boxShadow: "0 0 12px rgba(232,200,74,0.15)",
                  }} />
                </div>
              )}

              {/* ── BOTTOM PANEL: Room Temperature — hanya di generate mode ── */}
              {!isFinalMode && (
                <div style={{
                  flex: 1,
                  borderRadius: "20px",
                  background: speakingRow !== null && speakingRow >= 0 ? "rgba(232,200,74,0.05)" : "rgba(255,255,255,0.02)",
                  border: speakingRow !== null && speakingRow >= 0
                    ? "2px solid rgba(232,200,74,0.3)"
                    : "2px solid rgba(255,255,255,0.1)",
                  display: "flex", flexDirection: "column",
                  overflow: "hidden", position: "relative",
                  transition: "border 0.4s ease, background 0.4s ease",
                }}>
                  <Corners color={speakingRow !== null && speakingRow >= 0 ? "rgba(232,200,74,0.4)" : "rgba(62,148,210,0.35)"} />

                  {/* Panel label + speaking indicator */}
                  <div style={{
                    position: "absolute",
                    top: "clamp(16px, 2vh, 36px)",
                    left: "50%", transform: "translateX(-50%)",
                    fontSize: "clamp(28px, 1.8vw, 52px)",
                    letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: "700",
                    color: speakingRow !== null && speakingRow >= 0 ? "rgba(232,200,74,0.8)" : "rgba(255,255,255,0.7)",
                    whiteSpace: "nowrap", transition: "color 0.3s ease",
                    display: "flex", alignItems: "center", gap: "16px",
                  }}>
                    ✦ Room Temperature
                    {speakingRow !== null && speakingRow >= 0 && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                        {[0,1,2,3,4].map(j => (
                          <span key={j} style={{
                            display: "inline-block",
                            width:  "clamp(4px, 0.25vw, 6px)",
                            height: "clamp(14px, 1.8vh, 36px)",
                            background: "#e8c84a",
                            borderRadius: "4px",
                            boxShadow: "0 0 8px rgba(232,200,74,0.6)",
                            animation: `waveBar 0.65s ease-in-out ${j * 0.07}s infinite alternate`,
                          }} />
                        ))}
                        <span style={{
                          marginLeft: "10px",
                          fontSize: "clamp(24px, 2.2vh, 48px)",
                          animation: "pulse 1.2s ease-in-out infinite",
                        }}>
                          🔊
                        </span>
                      </span>
                    )}
                  </div>

                  {/* Content area */}
                  <div style={{
                    flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "clamp(60px, 8vh, 140px) clamp(3vw, 4vw, 8vw) clamp(16px, 2vh, 36px)",
                  }}>
                    {activeQuestion ? (
                      <p
                        key={activeQuestion.id}
                        style={{
                          margin: 0,
                          fontSize: "clamp(26px, 2.2vw, 72px)",
                          fontWeight: "600",
                          color: speakingRow !== null && speakingRow >= 0 ? "#fff" : "rgba(255,255,255,0.92)",
                          lineHeight: "1.35", textAlign: "center",
                          transition: "color 0.3s ease",
                          animation: "entranceRight 0.65s cubic-bezier(0.2,0,0,1) forwards",
                          textShadow: speakingRow !== null && speakingRow >= 0
                            ? "0 0 60px rgba(255,255,255,0.2)"
                            : "none",
                        }}
                      >
                        {activeQuestion.en}
                      </p>
                    ) : (
                      <p style={{
                        margin: 0,
                        fontSize: "clamp(20px, 1.4vw, 38px)",
                        color: "rgba(255,255,255,0.15)",
                        letterSpacing: "0.2em", textTransform: "uppercase",
                      }}>
                        Awaiting Room Temperature...
                      </p>
                    )}
                  </div>

                  {speakingRow !== null && speakingRow >= 0 && (
                    <div style={{
                      position: "absolute", inset: 0, pointerEvents: "none",
                      background: "radial-gradient(ellipse at 50% 65%, rgba(232,200,74,0.09) 0%, transparent 60%)",
                    }} />
                  )}
                </div>
              )}
            </div>

            {/* Bottom ornament */}
            <div style={{
              position: "relative", zIndex: 2,
              padding: "0 2vw clamp(10px, 1vh, 20px)",
              display: "flex", justifyContent: "center",
            }}>
              <OrnamentLine />
            </div>
          </div>
        )}
      </div>

      {/* ── GLOBAL STYLES ── */}
      <style>{`
        .ailive-content div,
        .ailive-content span,
        .ailive-content p {
          background-color: transparent !important;
          background:        transparent !important;
          padding:           0           !important;
          margin:            0           !important;
          color:             inherit     !important;
          font-family:       inherit     !important;
          font-size:         inherit     !important;
          line-height:       inherit     !important;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes entranceLeft {
          from { opacity: 0; transform: translateX(-48px) scale(0.96); }
          to   { opacity: 1; transform: translateX(0)     scale(1);    }
        }
        @keyframes entranceRight {
          from { opacity: 0; transform: translateX(48px)  scale(0.96); }
          to   { opacity: 1; transform: translateX(0)     scale(1);    }
        }
        @keyframes spinCircle {
          from { transform: rotate(0deg);   }
          to   { transform: rotate(360deg); }
        }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40%           { opacity: 1;   transform: scale(1.4); }
        }
        @keyframes waveBar {
          from { height: 4px;  opacity: 0.4; }
          to   { height: 32px; opacity: 1;   }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1;   }
          50%      { opacity: 0.35; }
        }
        @keyframes livePulse {
          0%, 100% { box-shadow: 0 0 8px rgba(232,200,74,1), 0 0 22px rgba(232,200,74,0.75), 0 0 48px rgba(232,200,74,0.35); }
          50%      { box-shadow: 0 0 12px rgba(232,200,74,1), 0 0 36px rgba(232,200,74,0.9), 0 0 80px rgba(232,200,74,0.5); }
        }

        @media (min-resolution: 2dppx) {
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        }

        html, body {
          overflow: hidden;
          margin: 0; padding: 0;
        }
      `}</style>
    </div>
  );
}